import {
  ArchiveBoxIcon,
  BookOpenIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  HandThumbUpIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import type { GetCommentParams } from '../../../../../api/comments';
import {
  archiveClientApplication,
  getClientApplication,
  getClientApplicationComments,
  patchClientApplication,
  postClientApplicationComments,
} from '../../../../../api/Rover/clientApplication';
import { clientApplicationStatus } from '../../../../../constants/Rover/Applications';
import { usePatchReadNotification } from '../../../../../hooks/ReactQueryHooks/useNotificationQuery';
import { usePermission } from '../../../../../hooks/usePermission';
import PageNotFound from '../../../../../pages/PageNotFound';
import { notificationSelector } from '../../../../../store';
import { notifyUser } from '../../../../../store/Toast';
import { convertEnumToText } from '../../../../../utils';
import Comments from '../../../../Comments';
import { HasPermission } from '../../../../Permission/HasPermission';
import DropDown, { type DropDownMenuItem } from '../../../../UI/DropDown';
import LoadingTdmDetail from '../../../../UI/loading/LoadingTdmDetail';
import Confirm from '../../../../UI/Modal/Confirm';
import ApplicationTimeline from '../../ApplicationTimeline';
import Styles from '../CCLFDevelopersIntake.module.css';
import CCLFApplicationInformation from './CCLFApplicationInformation';
import CCLFApplicationLoan from './CCLFApplicationLoan';
import CCLFDisclosuresReferences from './CCLFDisclosuresReferences';
import OrganizationExperience from './OrganizationExperience';

export default function CCLFDeveloperApplication() {
  const { id: applicationId } = useParams<string>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errorCode, setErrorCode] = useState<number>(0);

  const { hasPermission } = usePermission();
  const patchNotification = usePatchReadNotification();
  const notification = useSelector(notificationSelector);

  const fetchClientApplication = useQuery(
    ['get-client-application', applicationId],
    () => getClientApplication({ applicationId: Number(applicationId) }),
    {
      onSuccess: () => {
        if (notification.notificationId > 0) {
          patchNotification.mutate();
        }
        setErrorCode(0);
      },
      onError: (error: any) => {
        setErrorCode(error.response.status);
      },
      staleTime: 5000,
    }
  );

  const clientApplication = fetchClientApplication?.data?.data;

  const postComments = useCallback(
    async (values: { comment: string }) => {
      const response = await postClientApplicationComments({
        data: {
          resource_id: Number(applicationId),
          resource_type: 'ClientApplication',
          ...values,
        },
      });
      return response;
    },
    [applicationId]
  );

  const getComments = useCallback(
    (_params: GetCommentParams) => {
      return getClientApplicationComments({
        params: {
          resource_id: Number(applicationId),
          resource_type: 'ClientApplication',
        },
      });
    },
    [applicationId]
  );

  const changeApplicationStatus = useMutation(
    (data: { status: string }) =>
      patchClientApplication({
        data,
        applicationId: Number(applicationId),
      }),

    {
      onSuccess: () => {
        queryClient.invalidateQueries(['get-client-application']);
        queryClient.invalidateQueries(['ClientApplication-application-audits']);
      },
    }
  );

  const statusChangeHandler = (status: string) => {
    changeApplicationStatus.mutate({ status });
  };

  const statusChangeList: DropDownMenuItem[] = [
    {
      name: 'Under Review',
      clicked: () => statusChangeHandler(clientApplicationStatus.UNDER_REVIEW),
      icon: <BookOpenIcon className='h-5 w-5' />,
    },
    {
      name: 'Document Requested',
      clicked: () => statusChangeHandler(clientApplicationStatus.DOCUMENT_REQUESTED),
      icon: <DocumentTextIcon className='h-5 w-5' />,
    },
    {
      name: 'Deny',
      clicked: () => statusChangeHandler(clientApplicationStatus.DENIED),
      icon: <NoSymbolIcon className='h-5 w-5' />,
    },
    {
      name: 'Approve',
      clicked: () => statusChangeHandler(clientApplicationStatus.APPROVED),
      icon: <HandThumbUpIcon className='h-5 w-5' />,
    },
  ];

  const clientApplicationArchive = useMutation(() => archiveClientApplication(Number(applicationId)), {
    onSuccess: () => {
      queryClient.invalidateQueries(['get-partner-proposals']);
      navigate('/app/rover/client-applications', { replace: true });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Client Application Archive successful',
          toastType: 'success',
        })
      );
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Client Application Archive failed',
          toastType: 'failed',
        })
      );
    },
  });

  const archiveClientApplicationHandler = () => {
    setShowDeleteConfirm(true);
  };

  const confirmArchiveClientApplication = async () => {
    setShowDeleteConfirm(false);
    clientApplicationArchive.mutate();
  };

  const cancelArchiveClientApplication = () => {
    setShowDeleteConfirm(false);
  };

  if (errorCode === 404 && !fetchClientApplication.isLoading) {
    return <PageNotFound statusCode='404' title='Resource Not found' subtitle='Client Application does not exist any longer' />;
  }
  return (
    <>
      {showDeleteConfirm && (
        <Confirm
          title='Archive Client Application'
          message={'You are about to archive Client Application. Are you sure you want to archive ?'}
          acceptButtonLabel='Archive'
          declineButtonLabel='Cancel'
          accept={confirmArchiveClientApplication}
          decline={cancelArchiveClientApplication}
        />
      )}
      <div className='min-h-full max-w-screen-2xl mx-auto'>
        {fetchClientApplication.isSuccess && (
          <main>
            <div className='w-full mx-auto px-4 sm:px-6 flex items-center justify-between md:justify-end md:space-x-5 lg:px-8'>
              <div className='flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3 z-10'>
                {hasPermission(['UPDATE_CLIENT_APPLICATION']) && (
                  <DropDown menuItems={statusChangeList}>
                    <div className={`${Styles[clientApplication.status!]}`}>
                      <span className='font-bold capitalize'>{convertEnumToText(clientApplication.status)}</span>
                      <ChevronDownIcon className='h-4 w-4' />
                    </div>
                  </DropDown>
                )}
                {!hasPermission(['UPDATE_CLIENT_APPLICATION']) && (
                  <div className={`${Styles[clientApplication.status!]}`}>
                    <span className='font-bold capitalize'>{convertEnumToText(clientApplication.status)}</span>
                  </div>
                )}
              </div>
              <HasPermission of='ARCHIVE_CLIENT_APPLICATION'>
                <button
                  className='flex gap-1 rounded-full bg-tertiaryColor-normal px-4 py-2 text-white font-semibold hover:bg-tertiaryColor-medium cursor-pointer'
                  onClick={archiveClientApplicationHandler}
                >
                  <ArchiveBoxIcon className='h-5 w-5' />
                  <div className='hidden md:block'>Archive</div>
                </button>
              </HasPermission>
            </div>

            <div className='mt-8 w-full mx-auto grid grid-cols-1 gap-6 sm:px-6 lg:grid-flow-col-dense lg:grid-cols-6'>
              {clientApplication && (
                <div className='space-y-6 col-span-4'>
                  <CCLFApplicationInformation organizationInformation={clientApplication.orgInfo} files={clientApplication.files} />
                  <OrganizationExperience organizationExperience={clientApplication.orgExperience} files={clientApplication.files} />
                  <CCLFApplicationLoan financialRequest={clientApplication.financialRequest} />
                  <CCLFDisclosuresReferences
                    disclosuresReferences={clientApplication.disclosuresReferences}
                    files={clientApplication.files}
                  />
                  <Comments getComments={getComments} postComments={postComments} queryKey='client-application-comments' />
                </div>
              )}
              <div className='col-span-2 h-screen/2'>
                <ApplicationTimeline activity={clientApplication} resourceType='ClientApplication' />
              </div>
            </div>
          </main>
        )}
        {fetchClientApplication.isLoading && <LoadingTdmDetail />}
      </div>
    </>
  );
}
